import { createFlagOverrideStorage } from '@quickmd-llc/design-system'
import { defaultFlags, type Flags } from '@/store/flags/defaultFlags'
import { isDeployedToProduction } from '@/utils/isDeployedToProduction'

// Namespaced per app rather than shared, so two QuickMD apps served from the same origin in a
// preview environment cannot pin each other's flags.
const STORAGE_KEY = 'devToolsFlagOverrides'

export type FlagKey = keyof Flags

export const FLAG_KEYS: FlagKey[] = Object.keys(defaultFlags)
  .filter(isFlagKey)
  .sort((left, right) => left.localeCompare(right))

/**
 * Overrides are gated on the *deployment*, not on `isDevToolsEnabled`.
 *
 * Dev Tools are deliberately reachable in production — `getIsDevToolsEnabled` opens them for
 * anyone with `?devTools=true` or the localStorage key, which is how production issues get
 * inspected. A panel that could also rewrite flags there would be a live change to what admins
 * see, from a URL parameter. So the override layer keys off the hostname instead and is inert
 * everywhere production is served, however Dev Tools were opened.
 */
export const areFlagOverridesAllowed = () => !isDeployedToProduction

/**
 * Whether `key` names a real flag.
 *
 * Exported because the shared `FlagsPanel` is deliberately flag-type-agnostic and hands back a
 * plain `string` — this is what narrows it before it reaches the store.
 */
export function isFlagKey(key: string): key is FlagKey {
  return Object.hasOwn(defaultFlags, key)
}

// The storage half is shared with the other clients, which all needed the same pinning
// behaviour. It deals in `Record<string, unknown>` because it has no idea what shape any given
// flag has; the typed surface below is what this app owns.
const storage = createFlagOverrideStorage({
  storageKey: STORAGE_KEY,
  isAllowed: areFlagOverridesAllowed,
  isValidKey: isFlagKey,
})

// Built by `Object.assign` because the key/value pairing is only known at runtime, and so cannot
// be stated to the compiler without an assertion, which this repo's lint config forbids.
function asFlags(overrides: Record<string, unknown>): Partial<Flags> {
  const flags: Partial<Flags> = {}
  Object.assign(flags, overrides)

  return flags
}

/**
 * The flags currently pinned from the Dev Tools panel. Always empty in production.
 *
 * Stale keys are dropped rather than passed through: a flag that has since been renamed or
 * removed would otherwise be resurrected into the store with a shape the app no longer expects.
 */
export function getFlagOverrides(): Partial<Flags> {
  return asFlags(storage.getFlagOverrides())
}

/** Pins a flag to `value` until it is reset. Returns the full override set after the change. */
export function setFlagOverride(key: FlagKey, value: unknown): Partial<Flags> {
  return asFlags(storage.setFlagOverride(key, value))
}

export function clearFlagOverrides() {
  storage.clearFlagOverrides()
}
